'use client'

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { WhiteLabelAssets, BrandedContent } from '../white-label-assets'
import { useThemeStore } from '@/lib/stores/theme-store'
import { LoginVideo } from './login-video'

interface LoginCardProps {
  children: React.ReactNode
  titleShowcase: string
  brandName: string
  videoUrl?: string
}

export function LoginCard({ children, titleShowcase, brandName, videoUrl }: LoginCardProps) {
  const { t } = useTranslation(['auth'])
  const { currentTheme } = useThemeStore()
  const [isVertical, setIsVertical] = useState(false)

  // Check if logoNavOnly is enabled (both new and legacy structures)
  const logoNavOnly =
    currentTheme?.login?.features?.logoNavOnly ||
    (currentTheme as any)?.showcase_content?.login?.logoNavOnly

  const handleLogoLoad = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const { naturalHeight, naturalWidth } = event.currentTarget
    if (naturalHeight > naturalWidth) {
      setIsVertical(true)
    }
  }

  return (
    <Card
      className="max-w-sm md:min-w-[380px] w-full shadow-2xl backdrop-blur-sm border overflow-hidden"
      style={{
        backgroundColor: 'var(--login-form-bg)',
        color: 'var(--login-form-text)',
        borderColor: 'var(--login-form-border)',
      }}
    >
      {/* Vídeo no topo do card (se existir) */}
      {videoUrl && (
        <div className="w-full aspect-video">
          <LoginVideo url={videoUrl} className="h-full w-full" />
        </div>
      )}

      <CardHeader className="text-center flex flex-col items-start">
        {!logoNavOnly && !videoUrl && (
          <div className={`flex justify-start ${isVertical ? 'mb-6 self-center' : 'mb-20'}`}>
            <BrandedContent fallback={<Skeleton className="h-7 w-32" />}>
              <WhiteLabelAssets
                type="logo-form"
                className={isVertical ? 'h-32 w-auto' : 'h-7 w-auto'}
                alt={`${brandName} Logo`}
                onLoad={handleLogoLoad}
              />
            </BrandedContent>
          </div>
        )}

        <div className="space-y-1">
          <CardTitle
            className="text-3xl font-bold text-left"
            style={{ color: 'var(--login-body-title)' }}
          >
            {titleShowcase}
          </CardTitle>
        </div>

        <CardDescription
          className="text-left"
          style={{ color: 'var(--login-body-description)' }}
        >
          {t('auth:login.subtitle')}
        </CardDescription>
      </CardHeader>

      <CardContent
        style={{
          backgroundColor: 'var(--login-form-bg)',
          color: 'var(--login-form-text)',
        }}
      >
        {children}
      </CardContent>

      {/* Login footer */}
      <div
        className="px-6 py-4 text-center text-xs"
        style={{
          backgroundColor: 'var(--login-form-footer-bg)',
          color: 'var(--login-form-footer-text)',
        }}
      >
        {t('auth:login.noAccount')}{' '}
        <a
          href="/cadastro"
          style={{ color: 'var(--login-form-register)' }}
          className="font-medium hover:underline"
        >
          {t('auth:login.createAccount')}
        </a>
      </div>
    </Card>
  )
}

